export default function SelectionGuide() {
  const steps = [
    {
      number: '01',
      title: '年会費が無料か確認する',
      description: '維持コストがかからないカードを選ぶことで、長期的にお得に利用できます。',
      icon: 'ri-money-dollar-circle-line',
      color: 'from-emerald-500 to-teal-500'
    },
    {
      number: '02',
      title: 'ポイント還元率を見る',
      description: '基本還元率が1%以上のカードを選ぶと、効率よくポイントが貯まります。',
      icon: 'ri-percent-line',
      color: 'from-amber-500 to-orange-500'
    },
    {
      number: '03',
      title: 'よく使う店舗との相性',
      description: 'コンビニ、スーパー、ネットショップなど、よく使う場所で高還元のカードを選びましょう。',
      icon: 'ri-store-2-line',
      color: 'from-rose-500 to-pink-500'
    },
    {
      number: '04',
      title: 'キャンペーン特典をチェック',
      description: '入会特典で数千円分のポイントがもらえることも。タイミングを逃さずに申し込みましょう。',
      icon: 'ri-gift-line',
      color: 'from-violet-500 to-purple-500'
    }
  ];

  return (
    <section className="py-12 sm:py-16 md:py-20 lg:py-24 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
      {/* 装飾要素 */}
      <div className="absolute top-20 right-10 w-72 h-72 bg-gradient-to-br from-teal-100/50 to-emerald-100/50 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 left-10 w-80 h-80 bg-gradient-to-tr from-emerald-100/40 to-teal-100/40 rounded-full blur-3xl"></div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
        {/* ヘッダー */}
        <div className="text-center mb-10 sm:mb-12 md:mb-16">
          <span className="inline-flex items-center gap-2 bg-gradient-to-r from-teal-100 to-emerald-100 text-teal-700 px-4 sm:px-5 py-2 rounded-full text-xs sm:text-sm font-bold mb-4 sm:mb-6 shadow-sm">
            <i className="ri-lightbulb-line text-base sm:text-lg"></i>
            選び方ガイド
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4 sm:mb-5 tracking-tight px-4">
            クレジットカードの
            <br className="sm:hidden" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-emerald-600">選び方</span>
          </h2>
          <p className="text-base sm:text-lg text-gray-500 max-w-2xl mx-auto px-4">
            失敗しないためには「目的別」で選ぶのが重要です
          </p>
        </div>

        {/* ステップグリッド */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 mb-12 sm:mb-16">
          {steps.map((step) => (
            <div
              key={step.number}
              className="group bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 hover:border-teal-200 hover:-translate-y-1"
            >
              <div className="flex items-start gap-4 sm:gap-6">
                {/* ナンバー */}
                <div className="flex-shrink-0 relative">
                  <div className={`w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br ${step.color} rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    <span className="text-white text-lg sm:text-xl font-bold">{step.number}</span>
                  </div>
                </div>
                
                {/* コンテンツ */}
                <div className="flex-1">
                  <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                    <div className={`w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br ${step.color} bg-opacity-10 rounded-lg sm:rounded-xl flex items-center justify-center`}>
                      <i className={`${step.icon} text-lg sm:text-xl bg-gradient-to-br ${step.color} bg-clip-text text-transparent`} style={{ WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundImage: `linear-gradient(to bottom right, var(--tw-gradient-stops))` }}></i>
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 group-hover:text-teal-600 transition-colors">
                      {step.title}
                    </h3>
                  </div>
                  <p className="text-gray-600 leading-relaxed text-sm sm:text-base">
                    {step.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* まとめカード */}
        <div className="relative bg-gradient-to-br from-teal-600 via-teal-700 to-emerald-700 rounded-2xl sm:rounded-3xl p-8 sm:p-10 md:p-12 text-center text-white shadow-2xl overflow-hidden">
          {/* 装飾 */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-emerald-400/10 rounded-full blur-3xl"></div>
          
          <div className="relative max-w-3xl mx-auto">
            <div className="text-4xl sm:text-5xl mb-4 sm:mb-6">💡</div>
            <h3 className="text-xl sm:text-2xl md:text-3xl font-bold mb-4 sm:mb-5 px-4">
              迷ったら年会費無料×高還元率を選ぼう
            </h3>
            <p className="text-base sm:text-lg opacity-90 leading-relaxed mb-6 sm:mb-8 px-4">
              初めてのクレジットカードなら、年会費無料で還元率1%以上のカードがおすすめ。
              <br className="hidden sm:block" />
              リスクなく始められて、しっかりポイントも貯まります。
            </p>
            <a
              href="#ranking-section"
              className="inline-flex items-center justify-center gap-2 bg-white text-teal-700 font-bold px-6 sm:px-8 py-3 sm:py-4 rounded-full hover:bg-gray-100 transition-all shadow-lg whitespace-nowrap cursor-pointer text-sm sm:text-base w-full sm:w-auto"
            >
              <i className="ri-trophy-line text-lg sm:text-xl"></i>
              おすすめランキングを見る
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}